import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Typography, Box, Button, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'services/localization';
import Spinner from 'components/Spinner';
import {
	getInventoryFieldsArray,
	InventoryField,
	InventoryFields,
	SkuItem,
} from 'common/modules/inventory';
import {
	ArticleImport,
	ColumnImportData,
	getArticlesImportData,
	getColumnImportData,
	getSkuImportData,
	SkuImport,
} from './parse';
import * as UserSelectors from 'selectors/UserSelectors';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as NotificationActions from 'actions/NotificationActions';
import * as InventoryActions from 'actions/InventoryActions';
import { useDispatch, useSelector } from 'react-redux';
import {
	CsvUploadData,
	getExistingInventoryData,
	hasArticleChanges,
	hasColumnChanges,
	hasSkuChanges,
	isValidImport,
	updateArticles,
	updateInventoryColumns,
	updateSkus,
} from './utils';
import SetPageTitle from 'components/Portals/SetPageTitle';
import { Redirect } from 'react-router-dom';
import { Routes } from 'routing';
import SetPageNavAction from 'components/Portals/SetPageNavAction';
import { LoadingData } from 'common/types';
import ContainerBox from 'common/components/layout/ContainerBox';
import UpdatedSkusTable from './components/UpdatedSkusTable';
import AddedSkusTable from './components/AddedSkusTable';
import AddedArticlesTable from './components/AddedArticlesTable';
import UpdatedArticlesTable from './components/UpdatedArticlesTable';
import InvalidArticlesTable from './components/InvalidArticlesTable';
import InvalidSkusTable from './components/InvalidSkusTable';
import history from 'services/history';
import { ImportTableVariant } from './importTableUtils';
import Container from 'common/components/Container';
import AddedColumns from './components/AddedColumns';
import UpdatedColumns from './components/UpdatedColumnts';
import InvalidColumns from './components/InvalidColumns';
import { RiPlayListAddLine, RiRefreshLine, RiAlertFill } from 'react-icons/ri';

interface Props {
	csvUpload?: CsvUploadData;
	skuItems: SkuItem[];
	inventoryFields: InventoryFields;
	shopId: string;
}

export interface ImportData {
	articles: ArticleImport;
	columns: ColumnImportData;
	skus: SkuImport;
}

const getNewInventoryFields = (newData: ColumnImportData, existingData: InventoryField[]) => {
	const allCustomInventoryFields = [
		...existingData.map((field) => {
			const updatedFieldName = newData.toUpdate[field.id];
			return !updatedFieldName ? field : { ...field, label: updatedFieldName };
		}),
		...newData.toAdd,
	];
	return allCustomInventoryFields;
};

const getNewSkuItems = (newData: SkuImport, existingData: SkuItem[]): SkuItem[] => {
	const allSkuItems = [
		...existingData.map((item) => {
			const updatedSkuItem = newData.toUpdate.find((updatedSku) => updatedSku.data.id === item.id);
			return updatedSkuItem ? { ...item, ...updatedSkuItem.data } : item;
		}),
		...newData.toAdd.map((toAdd) => toAdd.data),
	];
	return allSkuItems;
};

const VerifyImport = (props: Props) => {
	const { t } = useTranslation();
	const classes = useStyles();
	const dispatch = useDispatch();
	const { csvUpload, shopId, skuItems } = props;
	const csvUploadData = useRef(csvUpload);
	const userLocations = useSelector(UserSelectors.userLocations);
	const allLocations = useSelector(ShopSelectors.activeShopAllLocations);
	const isMultiStore = allLocations.length > 1;
	const [importData, setImportData] = useState<LoadingData<ImportData | null>>({
		loading: false,
		data: null,
		error: null,
	});
	const [importLoading, setImportLoading] = useState<boolean>(false);
	const inventoryFieldsList = useMemo(() => getInventoryFieldsArray(props.inventoryFields), [
		props.inventoryFields,
	]);

	useEffect(() => {
		const getImportData = async (csvUpload: CsvUploadData) => {
			const { headerRow, dataRows } = csvUpload;

			const columnImportData = getColumnImportData(headerRow, inventoryFieldsList, t);
			const skuImportData = getSkuImportData(dataRows, skuItems, shopId, t);

			const { articles: existingArticles, inventoryData } = await getExistingInventoryData(
				dataRows,
				{
					shopId,
					locations: userLocations,
				},
			);

			const articlesImportData = getArticlesImportData({
				rows: dataRows,
				shopId,
				existingArticles,
				userLocations,
				skuItems: getNewSkuItems(skuImportData, skuItems),
				inventoryFields: getNewInventoryFields(columnImportData, inventoryFieldsList),
				isMultiStore,
				existingInventoryData: inventoryData,
				t,
			});

			return {
				articles: articlesImportData,
				skus: skuImportData,
				columns: columnImportData,
			};
		};
		const parseCsvUpload = async (csvUpload: CsvUploadData) => {
			setImportData((data) => ({
				loading: true,
				data: data.data,
				error: null,
			}));
			try {
				const data = await getImportData(csvUpload);
				setImportData({
					loading: false,
					data,
					error: null,
				});
			} catch (e) {
				setImportData({
					loading: false,
					data: null,
					error: t('common:errors.error'),
				});
			}
		};
		const uploadData = csvUploadData.current;
		if (!uploadData) return;
		parseCsvUpload(uploadData);
	}, [inventoryFieldsList, isMultiStore, shopId, skuItems, t, userLocations]);

	const handleCsvImport = async (data: ImportData | null) => {
		if (!data) return;
		setImportLoading(true);
		const massImport = [
			data.articles.toAdd.length,
			data.articles.toUpdate.length,
			data.skus.toAdd.length,
			data.skus.toUpdate.length,
		].some((length) => length > 30);
		if (massImport) {
			dispatch(InventoryActions.clearInventoryItemsListener());
			dispatch(InventoryActions.clearSkuItemsListener());
		}
		try {
			await Promise.all([
				updateInventoryColumns(data.columns, props.shopId),
				updateSkus(data.skus),
			]);
			await updateArticles(data.articles);
			setImportLoading(false);
			history.push(Routes.INVENTORY_ARTICLES);
			dispatch(
				NotificationActions.showNotification({
					variant: 'success',
					message: t('inventory.inventoryUpdated', 'Inventory updated'),
				}),
			);
		} catch (e) {
			setImportLoading(false);
			dispatch(
				NotificationActions.showNotification({
					variant: 'error',
					message: t('common:errors.somethingWentWrong'),
				}),
			);
		} finally {
			if (massImport) {
				dispatch(InventoryActions.setInventoryItemsListener(shopId));
				dispatch(InventoryActions.setSkuItemsListener(shopId));
			}
		}
		return;
	};

	if (!csvUpload) {
		return <Redirect to={Routes.INVENTORY_IMPORT} />;
	}

	if (importData.loading && !importData.data) {
		return <Spinner />;
	}

	if (!importData.data) {
		return null;
	}

	const noChanges =
		!hasArticleChanges(importData.data) &&
		!hasSkuChanges(importData.data) &&
		!hasColumnChanges(importData.data);

	return (
		<>
			<SetPageNavAction
				priority={4}
				backAction={{ to: Routes.INVENTORY_IMPORT, title: t('inventory.import.import', 'Import') }}
			/>
			<SetPageTitle
				priority={4}
				title={t('inventory.import.title', 'Inventory import')}
				rightContent={
					<Box display="flex" sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
						<Button
							variant="contained"
							onClick={() => handleCsvImport(importData.data)}
							sx={{ order: { sm: 2 }, marginLeft: { sm: 2 } }}
							disabled={!isValidImport(importData.data) || importLoading}
						>
							{importLoading ? (
								<Spinner padding={8} />
							) : (
								t('inventory.import.confirmImport', 'Confirm import')
							)}
						</Button>
						<Button
							variant="text"
							color="secondary"
							disabled={importLoading}
							onClick={() => history.push(Routes.INVENTORY_IMPORT)}
						>
							{t('common:actions.cancel')}
						</Button>
					</Box>
				}
			/>
			{noChanges ? (
				<Container>
					<Typography variant="h5" align="center">
						{t('inventory.import.noUpdates', 'No updates found')}
					</Typography>
					<Typography variant="body1" align="center" sx={{ mt: 2 }}>
						{t(
							'inventory.import.inventoryUpToDate',
							'Your inventory is up-to-date with the uploaded CSV file',
						)}
						.
					</Typography>
				</Container>
			) : (
				<>
					{hasArticleChanges(importData.data) && (
						<ContainerBox padding="none" className={classes.overflowHidden}>
							<ContainerHeader>{t('inventory.terms.articles', 'Articles')}</ContainerHeader>
							<Box px={4} my={4}>
								<Box mt={4} />
								{!!importData.data.articles.toAdd.length && (
									<>
										<SectionHeader
											name={t('inventory.import.newArticles', 'New Articles')}
											count={importData.data.articles.toAdd.length}
											type="toAdd"
										/>
										<AddedArticlesTable
											importData={importData.data.articles.toAdd}
											headerData={csvUpload.headerRow}
										/>
									</>
								)}
								<Box mt={4} />
								{!!importData.data.articles.toUpdate.length && (
									<>
										<SectionHeader
											name={t('inventory.import.updatedArticles', 'Updated Articles')}
											count={importData.data.articles.toUpdate.length}
											type="toUpdate"
										/>
										<UpdatedArticlesTable
											importData={importData.data.articles.toUpdate}
											headerData={csvUpload.headerRow}
										/>
									</>
								)}
								<Box mt={4} />
								{!!importData.data.articles.invalid.length && (
									<>
										<SectionHeader
											name={t('inventory.import.incorrectArticles', 'Incorrect Articles')}
											count={importData.data.articles.invalid.length}
											type="invalid"
										/>
										<InvalidArticlesTable
											importData={importData.data.articles.invalid}
											headerData={csvUpload.headerRow}
										/>
									</>
								)}
							</Box>
						</ContainerBox>
					)}
					{hasColumnChanges(importData.data) && (
						<ContainerBox padding="none" className={classes.overflowHidden}>
							<ContainerHeader>
								{t('inventory.terms.specifications', 'Specifications')}
							</ContainerHeader>
							<Box px={4} my={4}>
								<Box mt={4} />
								{!!importData.data.columns.toAdd.length && (
									<>
										<SectionHeader
											name={t('inventory.import.newSpecifications', 'New Specifications')}
											count={importData.data.columns.toAdd.length}
											type="toAdd"
										/>
										<AddedColumns importData={importData.data.columns.toAdd} />
									</>
								)}
								<Box mt={4} />
								{!!Object.keys(importData.data.columns.toUpdate).length && (
									<>
										<SectionHeader
											name={t('inventory.import.updatedSpecifications', 'Updated Specifications')}
											count={Object.keys(importData.data.columns.toUpdate).length}
											type="toUpdate"
										/>
										<UpdatedColumns
											importData={importData.data.columns.toUpdate}
											existingColumns={inventoryFieldsList}
										/>
									</>
								)}
								<Box mt={4} />
								{!!Object.keys(importData.data.columns.invalid).length && (
									<>
										<SectionHeader
											name={t(
												'inventory.import.incorrectSpecifications',
												'Incorrect Specifications',
											)}
											count={Object.keys(importData.data.columns.invalid).length}
											type="invalid"
										/>
										<InvalidColumns importData={importData.data.columns.invalid} />
									</>
								)}
							</Box>
						</ContainerBox>
					)}
					{hasSkuChanges(importData.data) && (
						<ContainerBox padding="none" className={classes.overflowHidden}>
							<ContainerHeader>{t('inventory.terms.skus', 'SKUs')}</ContainerHeader>
							<Box px={4} my={4}>
								<Box mt={4} />
								{!!importData.data.skus.toAdd.length && (
									<>
										<SectionHeader
											name={t('inventory.import.newSkus', 'New SKUs')}
											count={importData.data.skus.toAdd.length}
											type="toAdd"
										/>
										<AddedSkusTable
											importData={importData.data.skus.toAdd}
											headerData={csvUpload.headerRow}
										/>
									</>
								)}
								<Box mt={4} />
								{!!importData.data.skus.toUpdate.length && (
									<>
										<SectionHeader
											name={t('inventory.import.updatedSkus', 'Updated SKUs')}
											count={importData.data.skus.toUpdate.length}
											type="toUpdate"
										/>
										<UpdatedSkusTable
											importData={importData.data.skus.toUpdate}
											headerData={csvUpload.headerRow}
										/>
									</>
								)}
								<Box mt={4} />
								{!!importData.data.skus.invalid.length && (
									<>
										<SectionHeader
											name={t('inventory.import.incorrectSkus', 'Incorrect SKUs')}
											count={importData.data.skus.invalid.length}
											type="invalid"
										/>
										<InvalidSkusTable
											importData={importData.data.skus.invalid}
											headerData={csvUpload.headerRow}
										/>
									</>
								)}
							</Box>
						</ContainerBox>
					)}
				</>
			)}
		</>
	);
};

const useStyles = makeStyles((theme: Theme) => ({
	overflowHidden: {
		overflow: 'hidden',
	},
}));

const ContainerHeader: React.FC<{}> = ({ children }) => {
	return (
		<Box sx={{ py: 2, px: 4, backgroundColor: (theme) => theme.palette.background.default }}>
			<Typography variant="body1" sx={{ fontSize: 18 }}>
				{children}
			</Typography>
		</Box>
	);
};

const SectionHeader = ({
	name,
	count,
	type,
}: {
	name: string;
	count: number | null;
	type: ImportTableVariant;
}) => {
	const { t } = useTranslation();
	const icons: Record<ImportTableVariant, JSX.Element> = {
		toAdd: <RiPlayListAddLine size={20} />,
		toUpdate: <RiRefreshLine size={20} />,
		invalid: <RiAlertFill size={20} color="#F5B53E" />,
	};
	return (
		<Box display="flex" alignItems="flex-start" sx={{ mb: 2 }}>
			<Box display="flex" alignItems="center" mr={1}>
				{icons[type]}
			</Box>
			<Box>
				<Typography variant="body1" sx={{ lineHeight: 1.3 }}>
					{name} {count != null ? `(${count})` : ''}
				</Typography>
				{type === 'invalid' && (
					<Typography variant="body2" sx={{ fontWeight: 400, mt: 0.5 }} color="secondary">
						{t('inventory.import.willNotBeImported', 'These will not be imported')}
					</Typography>
				)}
			</Box>
		</Box>
	);
};

export default VerifyImport;
